/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { QuickMenuShortcut } from 'client-code/homeshortcut';

import { QuickMenuShortcutServiceRepositoryImpl } from '../impls/QuickMenuShortcutServiceRepositoryImpl';
import { IQuickMenuShortcutService } from '../interfaces/IQuickMenuShortcutService';
import { IQuickMenuShortcutServiceRepository } from '../interfaces/IQuickMenuShortcutServiceRepository';
import { Locale, LocaleKeys } from '../locale';
import { Log } from '../logging';
import { Storage } from '../storage';
import { TServicesParams } from '../types';
import { CcbSwitch } from '../utils/ccb';
import { CcbSwitchName } from '../utils/ccb/enums';

export class QuickMenuShortcutService implements IQuickMenuShortcutService {
    private static TAG = 'QuickMenuShortcutServiceImpl';

    private oQuickMenuShortcutServiceRepository: IQuickMenuShortcutServiceRepository;

    private mDefaultQuickMenuShortcuts: Array<QuickMenuShortcut> = [
        {
            quickMenuShortcutId: 'tivo:qmsc.4189',
            shortcutKey: 'num4',
            title: Locale.getString(LocaleKeys.SIDEBAR_NAVIGATION_MENU_GUIDE_OPTION_TEXT),
            uiAction: {
                destination: 'guide',
                uiActionType: 'ScreenDestinationUiAction',
            },
        },
        {
            quickMenuShortcutId: 'tivo:qmsc.4187',
            shortcutKey: 'num2',
            title: Locale.getString(LocaleKeys.SIDEBAR_NAVIGATION_MENU_MY_SHOWS_OPTION_TEXT),
            uiAction: {
                destination: 'myShows',
                uiActionType: 'ScreenDestinationUiAction',
            },
        },
        {
            quickMenuShortcutId: 'tivo:qmsc.490',
            shortcutKey: 'num6',
            title: Locale.getString(LocaleKeys.SIDEBAR_NAVIGATION_MENU_ON_DEMAND_OPTION_TEXT),
            uiAction: {
                destination: 'vodBrowse',
                uiActionType: 'ScreenDestinationUiAction',
            },
        },
        {
            quickMenuShortcutId: 'tivo:qmsc.4190',
            shortcutKey: 'num5',
            title: Locale.getString(LocaleKeys.SIDEBAR_NAVIGATION_MENU_SETTINGS_OPTION_TEXT),
            uiAction: {
                destination: 'settings',
                uiActionType: 'ScreenDestinationUiAction',
            },
        },
    ];

    public constructor(params: TServicesParams) {
        this.oQuickMenuShortcutServiceRepository = new QuickMenuShortcutServiceRepositoryImpl(
            params,
        );
    }

    /**
     * @inheritdoc
     */
    public async getQuickMenuShortcuts(): Promise<QuickMenuShortcut[]> {
        try {
            const cachedQuickMenuShortcuts = Storage.getInstance(
                Storage.StorageType.SESSION_STORAGE,
            ).getItem(Storage.Keys.QUICK_MENU_SHORTCUT);
            if (cachedQuickMenuShortcuts) return cachedQuickMenuShortcuts;

            const quickMenuShortcutResponse =
                await this.oQuickMenuShortcutServiceRepository.fetchQuickMenuShortcuts();
            if (!quickMenuShortcutResponse.length)
                throw new Error('Got a response without a "critical" shortcut');

            // store the response in session
            Storage.getInstance(Storage.StorageType.SESSION_STORAGE).setItem(
                Storage.Keys.QUICK_MENU_SHORTCUT,
                quickMenuShortcutResponse,
            );

            return quickMenuShortcutResponse;
        } catch (error: any) {
            Log.e(QuickMenuShortcutService.TAG, 'getQuickMenuShortcuts', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public getDefaultQuickMenuShortcuts(): Array<QuickMenuShortcut> {
        const isOnDemandEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
            CcbSwitchName.ENABLE_VOD_BROWSE_MAIN_SCREEN_REMOVE_IN_KST_12159,
        );
        return this.mDefaultQuickMenuShortcuts.filter((item) => {
            if (
                !isOnDemandEnabled &&
                item?.title ===
                    Locale.getString(LocaleKeys.SIDEBAR_NAVIGATION_MENU_ON_DEMAND_OPTION_TEXT)
            ) {
                return false;
            }
            return true;
        });
    }

    public getCachedQuickMenuShortcuts(): Array<QuickMenuShortcut> {
        const cachedQuickMenuShortcuts: Array<QuickMenuShortcut> = Storage.getInstance(
            Storage.StorageType.SESSION_STORAGE,
        ).getItem(Storage.Keys.QUICK_MENU_SHORTCUT);
        if (cachedQuickMenuShortcuts) return cachedQuickMenuShortcuts;
        return this.getDefaultQuickMenuShortcuts();
    }
}
